import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, Check, CheckCheck, ClipboardCheck, Crosshair, Download, FileText, Info,
  Loader2, Play, Sparkles, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import NumaSelect from "@/components/ui/numa-select";
import { checks, normalizeError } from "@/lib/api";
import { useLLM } from "@/contexts/LLMContext";
import AiBadge from "@/components/AiBadge";
import AttesaRevisione from "@/components/AttesaRevisione";
import GiocoNuma from "@/components/GiocoNuma";
import { ACTIONS as AI_READINGS } from "@/components/NumaPanel";
import { toast } from "sonner";

/**
 * A finished manuscript, read once, thoroughly.
 *
 * Three layers, kept visibly apart because they are not equally reliable:
 * what arithmetic can prove (references, structure, readability), what the
 * model finds in the language, and what it thinks of the argument. The first
 * is free and certain. The third is an opinion, and is labelled as one.
 *
 * Two ways out, because both are legitimate: accept a correction into the
 * document, or take the whole review away as a file and work through it in
 * Word.
 */

/*
  I rapporti già calcolati, tenuti da parte.

  Rianalizzare trecento pagine sono secondi di attesa, e rifarlo perché si è
  andati in editor a leggere una riga - senza cambiare niente - è tempo buttato.
  La chiave contiene `updated_at` del progetto, che cambia solo quando un
  capitolo viene salvato davvero: finché il documento è quello, il rapporto vale.
  Vive fuori dal componente perché il componente si smonta a ogni cambio di
  pagina, e la memoria deve sopravvivergli.
*/
const RAPPORTI = new Map();
const chiaveRapporto = (thesisId, impronta, scopeId, conAi) =>
  `${thesisId}|${scopeId}|${conAi ? "ai" : "base"}|${impronta}`;

/**
 * L'impronta del testo: due documenti uguali danno lo stesso numero.
 *
 * Prima la chiave del rapporto conteneva `updated_at`, la data dell'ultimo
 * salvataggio. Ma l'editor salva anche quando non si è cambiato niente - basta
 * aprirlo - e allora al ritorno in revisione la data era nuova, il rapporto
 * non valeva più e si ricominciava da capo: minuti di attesa per un testo
 * identico. L'impronta guarda il testo, non l'orologio.
 */
/**
 * Chiede al server, ogni mezzo secondo, a che punto è quella revisione.
 *
 * Restituisce una funzione da chiamare quando ha finito, per smettere. La
 * revisione vera è una sola richiesta che resta aperta: questa è l'unica cosa
 * che può dire, mentre dura, qualcosa di vero su quanto manca.
 */
function seguiAvanzamento(thesisId, pratica, mostra) {
  let vivo = true;
  const battito = setInterval(async () => {
    if (!vivo) return;
    const stato = await checks.reviewProgress(thesisId, pratica);
    if (vivo && stato && stato.totale) mostra(stato);
  }, 500);
  return () => { vivo = false; clearInterval(battito); };
}

/**
 * Il rapporto dopo che le correzioni sono entrate nel testo.
 *
 * Prima si rifaceva tutta la revisione: su trecento pagine erano altri minuti
 * di attesa dopo l'attesa dell'applicazione, e finché non finiva a schermo
 * restava l'elenco di prima - che è il motivo per cui sembrava non aver
 * applicato niente. Ma che cosa è stato corretto si sa già: sono i rilievi che
 * abbiamo appena mandato. Si tolgono, e i conti tornano subito.
 *
 * Non è un modo di nascondere il lavoro: la prossima revisione rileggerà il
 * testo per davvero, e se qualcosa non fosse entrato ricomparirebbe.
 */
function senzaIRilievi(rapporto, idApplicati, chiaviMeccaniche = []) {
  if (!rapporto) return rapporto;
  const tolti = new Set(idApplicati);
  const meccaniche = new Set(chiaviMeccaniche);
  const groups = (rapporto.groups || []).map((g) => {
    const rimasti = (g.findings || []).filter(
      (f) => !tolti.has(f.id) && !meccaniche.has(f.check),
    );
    if (rimasti.length === (g.findings || []).length) return g;
    const counts = { error: 0, warning: 0, info: 0 };
    rimasti.forEach((f) => { counts[f.severity] = (counts[f.severity] || 0) + 1; });
    return { ...g, findings: rimasti, counts };
  });
  return {
    ...rapporto,
    groups,
    total_findings: groups.reduce((somma, g) => somma + (g.findings || []).length, 0),
  };
}

function improntaDi(chapters) {
  let h = 5381;
  for (const c of chapters || []) {
    const pezzo = `${c.id || ""}\u0000${c.content || ""}`;
    for (let i = 0; i < pezzo.length; i += 1) {
      h = ((h * 33) ^ pezzo.charCodeAt(i)) >>> 0;
    }
  }
  return h.toString(36);
}

/**
 * I secondi trascorsi da quando il lavoro è cominciato.
 *
 * Un'attesa senza numero è più lunga di un'attesa contata: su trecento pagine
 * la revisione generica dura una decina di secondi, e senza niente che si
 * muova quei dieci secondi sembrano un blocco. Il numero dice anche quanto è
 * costato, il che serve a decidere se la prossima volta conviene una parte
 * sola invece dell'intero documento.
 */
function useCronometro(attivo) {
  const [secondi, setSecondi] = useState(0);
  useEffect(() => {
    if (!attivo) return undefined;
    const inizio = Date.now();
    setSecondi(0);
    const battito = setInterval(() => setSecondi((Date.now() - inizio) / 1000), 250);
    return () => clearInterval(battito);
  }, [attivo]);
  return secondi;
}

/** «4,2 s», «1 min 20 s»: come lo direbbe qualcuno, non come lo scrive un cronometro. */
function durataInParole(secondi) {
  const s = Math.max(0, Number(secondi) || 0);
  if (s < 10) return `${s.toFixed(1).replace(".", ",")} s`;
  if (s < 60) return `${Math.round(s)} s`;
  const minuti = Math.floor(s / 60);
  const resto = Math.round(s % 60);
  return resto ? `${minuti} min ${resto} s` : `${minuti} min`;
}

const SEVERITY = {
  error: { icon: XCircle, tone: "text-danger", label: "Da correggere" },
  warning: { icon: AlertTriangle, tone: "text-warn", label: "Da verificare" },
  info: { icon: Info, tone: "text-ink-subtle", label: "Da valutare" },
};

// Which layer produced a group, said in one word next to its heading.
const ORIGIN = {
  proofreading: { label: "letto dall'AI", tone: "text-assistant" },
  editorial: { label: "letto dall'AI", tone: "text-assistant" },
};

export default function ReviewView({ thesis, onChapterUpdate, onLocate, onRunAi }) {
  const { ensureKey, reportError, ready } = useLLM();
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(null);
  const [applied, setApplied] = useState({});
  const [openGroup, setOpenGroup] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [chosen, setChosen] = useState({});
  // Mechanical corrections - apostrophes, spacing - and whether they are wanted.
  const [tidyFixes, setTidyFixes] = useState([]);
  const [chosenTidy, setChosenTidy] = useState({});
  // A proposal from the model for one observation, waiting to be accepted.
  const [proposal, setProposal] = useState(null);
  const [rewriting, setRewriting] = useState(null);
  const [ignorando, setIgnorando] = useState(null);
  /*
    What to review, and whether to review it yet.

    `null` means the author has not chosen. Nothing is analysed until they do:
    on a manuscript of a hundred thousand words the free pass alone is seconds
    of work, and running it on arrival - and again on every change of scope -
    made the page look broken while it waited.
  */
  const [scope, setScope] = useState("tutte");
  const [loadingBasics, setLoadingBasics] = useState(false);
  // Quanto è durata l'ultima lettura, e a che punto è quella in corso.
  const [durata, setDurata] = useState(null);
  const [avanzamento, setAvanzamento] = useState(null);
  /*
    Una revisione che non è riuscita lo deve dire.

    Se qualcosa si rompe mentre si aspetta - e mentre si aspetta si può stare
    giocando, senza guardare la pagina - la revisione finiva in niente: tornava
    l'invito iniziale, identico a prima di cominciare, e sembrava che il lavoro
    non fosse mai partito. Ora resta scritto che cosa è successo, con il modo
    di riprovare da lì.
  */
  const [fallita, setFallita] = useState(null);
  /*
    Il passatempo, e il rispetto di chi non lo vuole.

    Si propone da solo dopo qualche secondo di attesa - è lì che serve - ma una
    volta chiuso non si riapre più da sé per tutta la sessione: resta il
    pulsante nel pannello, per chi cambia idea. La finestra sta qui e non nel
    pannello d'attesa perché deve sopravvivere alla fine della revisione: una
    partita interrotta di scatto è peggio di nessuna partita.
  */
  const [giocoAperto, setGiocoAperto] = useState(false);
  const rifiutato = useRef(false);
  const trascorso = useCronometro(loadingBasics || running);

  const chapters = thesis?.chapters || [];
  /*
    Ricalcolata solo quando il progetto dice di essere cambiato: scorrere
    trecento pagine di HTML costa qualche decina di millisecondi, e farlo a
    ogni disegno della pagina sarebbe uno spreco. Se il salvataggio è a vuoto,
    l'impronta torna identica e il rapporto di prima resta valido.
  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const impronta = useMemo(() => improntaDi(chapters), [thesis?.thesis_id, thesis?.updated_at]);

  /**
   * The free layer alone, so the page is never empty on arrival.
   *
   * `progetto` è il documento su cui fare i conti quando non è più quello che
   * questa funzione si è portata dietro: dopo aver applicato le correzioni il
   * testo è cambiato, e il rapporto nuovo va riposto sotto l'impronta nuova -
   * altrimenti si salva con la chiave di prima e al primo sguardo la pagina
   * sembra aver perso la revisione appena fatta.
   */
  const loadBasics = useCallback(async (chapterId = "", { rifai = false, progetto = null } = {}) => {
    const documento = progetto || thesis;
    if (!documento?.thesis_id) return;
    const segno = progetto ? improntaDi(progetto.chapters) : impronta;
    const chiave = chiaveRapporto(documento.thesis_id, segno, chapterId, false);
    if (!rifai && RAPPORTI.has(chiave)) {
      // Già calcolato su questo stesso testo: si rimostra invece di rifarlo.
      const salvato = RAPPORTI.get(chiave);
      setReport(salvato);
      setDurata(salvato.durata ?? null);
      const primo = (salvato.groups || []).find((g) => (g.findings || []).length);
      setOpenGroup((aperto) => (aperto && salvato.groups?.some((g) => g.key === aperto) ? aperto : primo?.key || ""));
      return;
    }
    setLoadingBasics(true);
    setAvanzamento(null);
    setFallita(null);
    const partito = Date.now();
    try {
      const res = await checks.reviewRun(
        documento.thesis_id, { chapterId, includeAi: false }, setAvanzamento,
      );
      res.durata = (Date.now() - partito) / 1000;
      setDurata(res.durata);
      RAPPORTI.set(chiave, res);
      setReport(res);
      const first = (res.groups || []).find((g) => (g.findings || []).length);
      setOpenGroup((open) => (open && res.groups?.some((g) => g.key === open) ? open : first?.key || ""));
    } catch (err) {
      /*
        Niente finestra rossa: la revisione è un lavoro lungo che si segue con
        la coda dell'occhio, e un guasto di rete a metà non è un'emergenza da
        interrompere quello che si sta facendo. Resta scritto nella pagina.
      */
      if (!err?.annullata) setFallita(normalizeError(err));
    } finally {
      setAvanzamento(null);
      setLoadingBasics(false);
    }
  }, [thesis, impronta]);

  const inAttesa = running || loadingBasics || bulkRunning;
  useEffect(() => {
    if (!inAttesa || rifiutato.current) return undefined;
    const quando = setTimeout(() => setGiocoAperto(true), 6000);
    return () => clearTimeout(quando);
  }, [inAttesa]);

  // The part of the document under review, in the form the API wants: the
  // whole thing is an empty chapter id.
  const scopeId = scope && scope !== "tutte" ? scope : "";

  /*
    Tornando sulla pagina, quello che c'era.

    Se il documento non è cambiato da quando è stato analizzato, il rapporto è
    ancora valido: si rimette a schermo senza rifare niente. Se è cambiato, la
    chiave non combacia più e la pagina torna al suo invito iniziale - premere
    «Visualizza revisione» rifà i conti sul testo nuovo.
  */
  useEffect(() => {
    if (!thesis?.thesis_id) return;
    const conAi = RAPPORTI.get(chiaveRapporto(thesis.thesis_id, impronta, scopeId, true));
    const senzaAi = RAPPORTI.get(chiaveRapporto(thesis.thesis_id, impronta, scopeId, false));
    const trovato = conAi || senzaAi || null;
    setReport(trovato);
    setDurata(trovato?.durata ?? null);
  }, [thesis?.thesis_id, impronta, scopeId]);

  // What can be corrected mechanically, counted on the scope being shown -
  // asked for only once there is a report, so that arriving on the page costs
  // nothing.
  useEffect(() => {
    if (!thesis?.thesis_id || !report) return;
    checks
      .tidyAvailable(thesis.thesis_id, scopeId)
      .then((res) => setTidyFixes((res.fixes || []).filter((f) => f.count > 0)))
      .catch(() => setTidyFixes([]));
    // Volutamente senza `applied`: l'elenco delle meccaniche cambia quando
    // cambia il rapporto, non a ogni singola correzione accettata - e con
    // `applied` fra le dipendenze partiva una richiesta per ogni clic.
  }, [thesis?.thesis_id, scopeId, report]);

  const runFull = async () => {
    if (!(await ensureKey())) return;
    setRunning(true);
    setAvanzamento(null);
    setFallita(null);
    const partito = Date.now();
    try {
      const res = await checks.reviewRun(
        thesis.thesis_id, { chapterId: scopeId, includeAi: true }, setAvanzamento,
      );
      res.durata = (Date.now() - partito) / 1000;
      setDurata(res.durata);
      RAPPORTI.set(chiaveRapporto(thesis.thesis_id, impronta, scopeId, true), res);
      setReport(res);
      const first = (res.groups || []).find((g) => (g.findings || []).length);
      setOpenGroup(first?.key || "");
      if (res.ai_error) {
        toast.info("Revisione parziale", {
          description: "I controlli automatici sono completi; la parte letta dal modello no.",
        });
      } else if (res.chapters_read < res.chapters_total) {
        toast.info(`Lette ${res.chapters_read} sezioni su ${res.chapters_total}`, {
          description: "Rilancia la revisione scegliendo le sezioni rimanenti.",
        });
      }
    } catch (err) {
      if (!err?.annullata) setFallita(normalizeError(err));
    } finally {
      setAvanzamento(null);
      setRunning(false);
    }
  };

  /**
   * A structural correction: heading levels, paragraphs broken by an import.
   *
   * These change the shape of the document rather than a passage of it, so
   * they travel by their own route - there is no excerpt to match, and nothing
   * of the author's prose is rewritten.
   */
  const applyStructure = async (finding) => {
    setApplying(finding.id);
    try {
      const res = await checks.applyStructure(thesis.thesis_id, [finding.fix]);
      if (!res.applied) {
        const why = res.skipped?.[0]?.reason || "correzione non applicabile";
        toast.error(`Non applicata: ${why}`);
        return;
      }
      setApplied((prev) => ({ ...prev, [finding.id]: true }));
      onChapterUpdate?.(res.thesis);
      toast.success(res.headings_fixed === 1
        ? "1 titolo allineato"
        : `${res.headings_fixed} titoli allineati`);
    } catch (err) {
      const e = normalizeError(err);
      toast.error(e.message, { description: e.action || undefined });
    } finally {
      setApplying(null);
    }
  };

  /**
   * Il passaggio, aperto in un'altra scheda.
   *
   * Prima la revisione lasciava il posto all'editor: per confrontare il rilievo
   * con il testo bisognava andare e tornare, e l'elenco dei rilievi ripartiva
   * da capo ogni volta. Due schede stanno l'una accanto all'altra.
   */
  const apriNelTesto = (finding) => {
    const parametri = new URLSearchParams({ progetto: thesis.thesis_id });
    if (finding.chapter_id) parametri.set("sezione", finding.chapter_id);
    if (finding.position != null) parametri.set("da", String(finding.position));
    if (finding.excerpt) parametri.set("cerca", finding.excerpt.slice(0, 120));
    window.open(`/?${parametri.toString()}`, "_blank", "noopener");
  };

  /**
   * Togliere un rilievo dall'elenco.
   *
   * Questi controlli vedono la forma, non il senso: una regola che non si può
   * zittire diventa rumore, e il rumore insegna a ignorare tutto l'elenco.
   * Stava solo nella pagina «Controlli», che ora non esiste più.
   */
  const ignora = async (finding) => {
    setIgnorando(finding.id);
    try {
      await checks.dismiss(thesis.thesis_id, finding.id, finding.check, true);
      setReport((precedente) => precedente && {
        ...precedente,
        total_findings: Math.max(0, (precedente.total_findings || 1) - 1),
        groups: (precedente.groups || []).map((g) => ({
          ...g,
          findings: (g.findings || []).filter((f) => f.id !== finding.id),
          dismissed: (g.findings || []).some((f) => f.id === finding.id)
            ? (g.dismissed || 0) + 1
            : g.dismissed,
        })),
      });
    } catch (err) {
      toast.error(normalizeError(err).message);
    } finally {
      setIgnorando(null);
    }
  };

  const apply = async (finding) => {
    setApplying(finding.id);
    try {
      const res = await checks.applySuggestion(thesis.thesis_id, {
        chapterId: finding.chapter_id,
        excerpt: finding.excerpt,
        replacement: finding.replacement,
        // Dove il rilievo diceva di trovarsi: serve quando lo stesso passaggio
        // compare più volte nella sezione.
        position: finding.position ?? null,
      });
      setApplied((prev) => ({ ...prev, [finding.id]: true }));
      onChapterUpdate?.(res.thesis);
      toast.success("Correzione applicata al testo");
    } catch (err) {
      const e = normalizeError(err);
      // A correction that cannot be placed safely is a thing to say, not a
      // thing to guess at.
      toast.error(e.message, { description: e.action || undefined });
    } finally {
      setApplying(null);
    }
  };

  /**
   * Every correction in the ticked categories, applied in one pass.
   *
   * Only the categories that actually produce replacement text can be ticked:
   * style, structure and the rest give advice in prose, and writing that into
   * the document would replace the author's words with a comment about them.
   */
  const applyAll = async () => {
    const wanted = groups
      .filter((g) => chosen[g.key])
      .flatMap((g) => g.findings)
      .filter((f) => f.replacement && f.excerpt && f.chapter_id && !applied[f.id]);
    const tidyKeys = tidyFixes.filter((f) => chosenTidy[f.key]).map((f) => f.key);
    if (!wanted.length && !tidyKeys.length && !chosenAdvice.length && !chosenStructure.length) return;
    // The rewrites cost a call each, so the model is asked for once, here.
    if (chosenAdvice.length && !(await ensureKey())) return;

    /*
      Che cosa resterà in elenco, e perché.

      Chi preme «applica tutte» si aspetta che dopo non resti niente. Alcuni
      rilievi però non sono sostituzioni: «periodo di 67 parole» non ha un
      testo con cui rimpiazzarlo, ce l'ha solo chi scrive. Dirlo subito evita
      di credere che le correzioni non siano state applicate.
    */
    const restanoDaScrivere = groups
      .flatMap((g) => g.findings || [])
      .filter((f) => !f.replacement && !f.fix && !applied[f.id]).length;
    const nota = (quanti) => (quanti === 1
      ? "1 rilievo resta in elenco: è un'osservazione, non una sostituzione, e chiede una riscrittura."
      : `${quanti} rilievi restano in elenco: sono osservazioni, non sostituzioni, e chiedono una riscrittura.`);
    let ultimoDocumento = thesis;
    /*
      Anche applicare è un'attesa, e un'attesa si conta.

      Su ventisei sezioni le sostituzioni sono migliaia, e finché duravano non
      si vedeva niente: la finestra restava lì, e sembrava bloccata. Ora il
      server dice a che sezione è arrivato, e questo è lo stesso numero di
      pratica che segue la revisione.
    */
    const pratica = `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    const smetti = seguiAvanzamento(thesis.thesis_id, pratica, setAvanzamento);

    /*
      Il rapporto aggiornato, riposto sotto l'impronta del testo nuovo.

      Si parte dal rapporto com'era adesso, congelato qui: durante
      l'applicazione il documento cambia sotto i piedi, e leggere lo stato
      «corrente» a metà strada voleva dire trovarlo già svuotato. Ed è una
      chiamata sola alla fine, non una per passaggio: ogni aggiornamento
      intermedio del progetto faceva ripartire la ricerca in memoria con
      un'impronta che non c'era ancora, e la pagina tornava all'invito
      iniziale come se la revisione non fosse mai stata fatta.
    */
    const rapportoPrima = report;
    const aggiorna = (documento, idApplicati, chiaviMeccaniche) => {
      const nuovo = senzaIRilievi(rapportoPrima, idApplicati, chiaviMeccaniche);
      if (documento?.thesis_id && nuovo) {
        RAPPORTI.set(
          chiaveRapporto(documento.thesis_id, improntaDi(documento.chapters), scopeId, false),
          nuovo,
        );
      }
      setReport(nuovo);
      // Solo adesso il resto dell'applicazione sa del testo nuovo: prima il
      // rapporto è al suo posto, poi il progetto cambia.
      if (documento) onChapterUpdate?.(documento);
    };

    setBulkRunning(true);
    setAvanzamento(null);
    // La finestra si chiude subito: l'avanzamento si vede nella pagina, dove
    // c'è spazio per dirlo - e dove c'è anche il passatempo, se l'attesa dura.
    setBulkOpen(false);
    try {
      // Structure first: adding a section moves nothing inside the others, and
      // doing it last would mean matching passages in a document that has since
      // grown a chapter.
      let structural = 0;
      if (chosenStructure.length) {
        const res = await checks.applyStructure(
          thesis.thesis_id, chosenStructure.map((f) => f.fix),
        );
        structural = res.applied;
        ultimoDocumento = res.thesis || ultimoDocumento;
        setApplied((prev) => ({
          ...prev,
          ...Object.fromEntries(chosenStructure.map((f) => [f.id, true])),
        }));
      }

      // The mechanical ones next: they change spacing and apostrophes, and a
      // quoted passage matched afterwards would no longer be found.
      let tidied = 0;
      if (tidyKeys.length) {
        const res = await checks.tidy(thesis.thesis_id, tidyKeys, scopeId, pratica);
        tidied = res.total;
        ultimoDocumento = res.thesis || ultimoDocumento;
      }
      // Then the rewrites: one model call per observation, bounded server-side.
      let rewritten = 0;
      if (chosenAdvice.length) {
        const res = await checks.applyAdvice(
          thesis.thesis_id,
          chosenAdvice.slice(0, 20).map((f) => ({
            id: f.id, chapter_id: f.chapter_id, excerpt: f.excerpt,
            title: f.title, detail: f.detail, suggestion: f.suggestion,
          }))
        );
        rewritten = res.applied;
        ultimoDocumento = res.thesis || ultimoDocumento;
        const done = new Set(
          chosenAdvice.map((f) => f.id)
            .filter((id) => !(res.skipped || []).some((x) => x.id === id))
        );
        setApplied((prev) => ({
          ...prev, ...Object.fromEntries([...done].map((id) => [id, true])),
        }));
      }

      if (!wanted.length) {
        setTidyFixes([]);
        aggiorna(ultimoDocumento, [
          ...chosenStructure.map((f) => f.id),
          ...chosenAdvice.map((f) => f.id),
        ], tidyKeys);
        toast.success(`${structural + tidied + rewritten} correzioni applicate`, {
          description: restanoDaScrivere ? nota(restanoDaScrivere) : undefined,
        });
        return;
      }
      const res = await checks.applyAll(
        thesis.thesis_id,
        // `position` è dove il rilievo diceva di trovarsi: serve al server per
        // scegliere l'occorrenza giusta quando il passaggio compare più volte.
        wanted.map((f) => ({
          id: f.id, chapter_id: f.chapter_id, excerpt: f.excerpt,
          replacement: f.replacement, position: f.position ?? null,
          // Il tipo di rilievo: un refuso si corregge ovunque compaia, una
          // proposta di stile solo dove il rilievo la fa.
          check: f.check || "",
        })),
        pratica,
      );
      const failed = new Set((res.skipped || []).map((x) => x.id));
      ultimoDocumento = res.thesis || ultimoDocumento;
      setApplied((prev) => ({
        ...prev,
        ...Object.fromEntries(wanted.filter((f) => !failed.has(f.id)).map((f) => [f.id, true])),
      }));
      setTidyFixes([]);
      aggiorna(ultimoDocumento, [
        ...wanted.filter((f) => !failed.has(f.id)).map((f) => f.id),
        ...chosenStructure.map((f) => f.id),
        ...chosenAdvice.map((f) => f.id),
      ], tidyKeys);

      /*
        Quante ne sono entrate, e basta.

        Prima si diceva anche quante no, con il motivo. Era un dettaglio di
        funzionamento - un passaggio che compariva due volte - presentato come
        un fallimento dell'autore, che non poteva farci niente. Ora entrano
        tutte quelle che nel testo ci sono ancora; quelle che non ci sono più
        sono già state corrette a mano, e non c'è niente da segnalare.
      */
      toast.success(`${res.applied + tidied + rewritten} correzioni applicate`, {
        description: restanoDaScrivere ? nota(restanoDaScrivere) : undefined,
      });

    } catch (err) {
      reportError(err);
    } finally {
      smetti();
      setAvanzamento(null);
      setBulkRunning(false);
    }
  };

  /**
   * Ask the model what to put in place of a passage.
   *
   * The deterministic checks say what to look at; turning "il periodo è lungo
   * 67 parole" into a shorter period is writing, and writing needs a model.
   * The proposal is shown, not applied: what replaces the author's words is
   * their decision.
   */
  const proposeRewrite = async (finding) => {
    if (!(await ensureKey())) return;
    setRewriting(finding.id);
    setProposal(null);
    try {
      const res = await checks.rewriteFinding(thesis.thesis_id, {
        chapter_id: finding.chapter_id,
        excerpt: finding.excerpt,
        title: finding.title,
        detail: finding.detail,
        suggestion: finding.suggestion,
      });
      setProposal({ id: finding.id, ...res, finding });
    } catch (err) {
      reportError(err);
    } finally {
      setRewriting(null);
    }
  };

  const acceptProposal = async () => {
    if (!proposal) return;
    setApplying(proposal.id);
    try {
      const res = await checks.applySuggestion(thesis.thesis_id, {
        chapterId: proposal.chapter_id,
        excerpt: proposal.excerpt,
        replacement: proposal.replacement,
        position: proposal.position ?? null,
      });
      setApplied((prev) => ({ ...prev, [proposal.id]: true }));
      onChapterUpdate?.(res.thesis);
      setProposal(null);
      toast.success("Passaggio riscritto");
    } catch (err) {
      const e = normalizeError(err);
      toast.error(e.message, { description: e.action || undefined });
    } finally {
      setApplying(null);
    }
  };

  const download = async () => {
    try {
      const blob = await checks.exportReview(
        thesis.thesis_id, report?.groups || [], report?.scope_title || ""
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // The section belongs in the file name: two reviews of the same document
      // otherwise land in the same folder under the same name.
      const scopeName = report?.scope_title ? ` - ${report.scope_title}` : "";
      link.download = `Revisione - ${thesis.title}${scopeName}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      reportError(err);
    }
  };

  const groups = (report?.groups || []).filter((g) => (g.findings || []).length);
  const total = groups.reduce((sum, g) => sum + g.findings.length, 0);
  const current = groups.find((g) => g.key === openGroup) || null;
  // How many corrections each category can actually place in the text.
  const applicableBy = Object.fromEntries(
    groups.map((g) => [
      g.key,
      g.findings.filter((f) => f.replacement && f.excerpt && f.chapter_id && !applied[f.id]).length,
    ])
  );
  // Findings with no replacement of their own, which a model could rewrite.
  const advisableBy = Object.fromEntries(
    groups.map((g) => [
      g.key,
      g.findings.filter(
        (f) => !f.replacement && !f.fix && f.excerpt && f.chapter_id && !applied[f.id]
      ).length,
    ])
  );
  const tidyTotal = tidyFixes.reduce((sum, f) => sum + f.count, 0);
  const applicableTotal =
    Object.values(applicableBy).reduce((a, b) => a + b, 0) + tidyTotal;
  const chosenCount =
    groups.filter((g) => chosen[g.key]).reduce((sum, g) => sum + applicableBy[g.key], 0) +
    tidyFixes.filter((f) => chosenTidy[f.key]).reduce((sum, f) => sum + f.count, 0);
  // Structural corrections: applied without a model, and without rewriting a
  // line of the author's text.
  const structuralBy = Object.fromEntries(
    groups.map((g) => [g.key, g.findings.filter((f) => f.fix && !applied[f.id]).length])
  );
  const chosenStructure = groups
    .filter((g) => chosen[g.key])
    .flatMap((g) => g.findings)
    .filter((f) => f.fix && !applied[f.id]);
  /*
    The order in the batch panel: what is applied as it stands, then the
    structural changes - neither of which costs a call - and only then what the
    model would have to rewrite. Reading down the list is then reading down the
    price.
  */
  const orderedForApply = [...groups].sort((a, b) => {
    const rank = (g) => (applicableBy[g.key] ? 0 : structuralBy[g.key] ? 1 : 2);
    return rank(a) - rank(b);
  });
  const chosenAdvice = groups
    .filter((g) => chosen[g.key])
    .flatMap((g) => g.findings)
    .filter((f) => !f.replacement && !f.fix && f.excerpt && f.chapter_id && !applied[f.id]);
  const scopeLabel = scopeId
    ? `«${chapters.find((c) => c.id === scopeId)?.title || "sezione scelta"}»`
    : "quella aperta nell'editor";
  const aiDone = !!report?.ai_used;

  return (
    <div className="h-screen overflow-y-auto bg-paper numa-fade-in" data-testid="review-view">
      <div className="mx-auto max-w-4xl px-6 py-9 lg:px-10">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-serif-display text-[28px] font-semibold text-ink">Revisione</h1>
            <p className="numa-meta mt-1 max-w-3xl leading-relaxed">
              Una rilettura completa del documento: forma, lingua e merito.
            </p>
            <AiBadge
              mode="optional"
              className="mt-3"
              detail="Riferimenti, struttura e leggibilità sono calcolati in locale. Lingua e merito richiedono il modello collegato."
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/*
              Sempre mostrato, anche con una sezione sola.

              Nascondendolo quando il documento non era diviso, chi caricava un
              capitolo singolo si ritrovava senza selettore e con i pulsanti
              spenti: nessun modo di chiedere la revisione di niente. La scelta
              di partenza è «tutto il documento», che è sempre revisionabile.
            */}
            {chapters.length > 0 && (
              <NumaSelect
                testId="review-scope"
                aria-label="Parte da revisionare"
                value={scope || "tutte"}
                placeholder="Tutto il documento"
                onValueChange={(v) => { setScope(v); setReport(null); }}
                className="h-9 min-w-[15rem] text-[13px]"
                options={[
                  { value: "tutte", label: "Tutto il documento" },
                  ...chapters.map((c) => ({ value: c.id, label: c.title || "Senza titolo" })),
                ]}
              />
            )}
            {/*
              Two buttons, because they cost different things: the first is
              arithmetic and free, the second asks the model. Neither starts by
              itself - on a long manuscript that was a wait nobody had asked for.
            */}
            <Button
              variant="outline"
              onClick={() => loadBasics(scopeId, { rifai: true })}
              disabled={!scope || loadingBasics || running}
              data-testid="show-review"
              title="Controlli automatici: riferimenti, struttura, leggibilità, ortografia. Senza modello."
            >
              {loadingBasics
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <ClipboardCheck className="h-3.5 w-3.5" />}
              Visualizza revisione
            </Button>
            <Button onClick={runFull} disabled={!scope || running || loadingBasics} data-testid="run-review">
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              {aiDone ? "Rileggi" : "Revisione approfondita"}
            </Button>
          </div>
        </header>

        {!aiDone && (
          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface px-4 py-3.5">
            <Sparkles className="h-4 w-4 shrink-0 text-assistant" />
            <p className="min-w-0 flex-1 text-[13.5px] leading-relaxed text-ink">
              Qui sotto ci sono i controlli che NUMA fa da sé, senza costi.
              <span className="text-ink-muted">
                {" "}La rilettura della lingua e del contenuto — cosa manca, cosa si può
                togliere, dove l&apos;argomentazione è debole — richiede un modello
              {ready ? "" : ", che non hai ancora collegato"}.
              </span>
            </p>
          </div>
        )}

        {/*
          The readings a model gives back, where a review is being read.

          These used to live only in the right-hand rail, which on this page did
          not exist at all - so the most substantial thing the AI does here was
          reachable only by narrowing the window until the drawer appeared.
        */}
        <section className="mt-6">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="font-serif-display text-[17px] font-semibold text-ink">
              Chiedi una lettura a NUMA
            </h2>
            <span className="inline-flex items-center gap-1 text-2xs text-assistant">
              <Sparkles className="h-3 w-3" /> usa l&apos;AI
            </span>
          </div>
          <p className="numa-meta mt-1 leading-relaxed">
            Sulla sezione aperta, {scopeLabel}. Diverse dai rilievi automatici qui sotto:
            sono giudizi di un lettore, e vanno valutati.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {AI_READINGS.filter((a) => a.key !== "venue_fit").map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  type="button"
                  data-testid={`ai-reading-${action.key}`}
                  onClick={() => onRunAi?.(action.key)}
                  className="flex items-start gap-2.5 rounded-md border border-line bg-surface px-3.5 py-3 text-left transition-colors hover:border-assistant/50 hover:bg-assistant-soft/25"
                >
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-assistant" />
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-medium text-ink">{action.title}</span>
                    <span className="numa-meta mt-0.5 block leading-snug">{action.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {inAttesa && (
          <AttesaRevisione
            avanzamento={avanzamento}
            conAi={running}
            secondi={trascorso}
            onGioca={() => setGiocoAperto(true)}
          />
        )}

        <Dialog
          open={giocoAperto}
          onOpenChange={(aperto) => {
            setGiocoAperto(aperto);
            if (!aperto) rifiutato.current = true;
          }}
        >
          <DialogContent className="max-w-2xl" data-testid="gioco-dialogo">
            <DialogHeader>
              <DialogTitle>Mentre attendi, se vuoi, gioca con NUMA</DialogTitle>
              <DialogDescription>
                La revisione continua per conto suo. Puoi chiudere questa finestra quando
                vuoi: non si riapre da sola.
              </DialogDescription>
            </DialogHeader>
            <GiocoNuma pronto={!inAttesa} onChiudi={() => setGiocoAperto(false)} />
          </DialogContent>
        </Dialog>

        {fallita && !inAttesa && (
          <div className="mt-6 rounded-md border border-warn/40 bg-warn/10 px-4 py-3.5"
               data-testid="revisione-fallita">
            <p className="text-[13.5px] text-ink">
              La revisione non è arrivata in fondo. {fallita.message}
            </p>
            {fallita.action && <p className="numa-meta mt-1">{fallita.action}</p>}
            <button
              type="button"
              data-testid="riprova-revisione"
              onClick={() => loadBasics(scopeId, { rifai: true })}
              className="mt-2 text-[12.5px] font-medium text-brand hover:underline"
            >
              Riprova →
            </button>
          </div>
        )}

        {/*
          Nothing is analysed until it is asked for. On a long manuscript the
          free pass alone takes seconds, and doing it on arrival meant every
          visit to this page began with a wait for a report the author might
          not have wanted.
        */}
        {!report && !running && !loadingBasics && !fallita && (
          <div className="mt-6 rounded-md border border-dashed border-line-strong p-8 text-center">
            <ClipboardCheck className="mx-auto h-8 w-8 text-ink-subtle" />
            <p className="mt-3 text-[14px] text-ink">
              Pronto! Clicca su «Visualizza revisione».
            </p>
            <p className="numa-meta mx-auto mt-1 max-w-md leading-relaxed">
              La revisione generica non usa l&apos;AI e non costa nulla. Su un documento
              lungo, una parte alla volta è più rapida dell&apos;intero documento.
            </p>
          </div>
        )}

        {report && !running && (
          <>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-3">
              <p className="text-[13.5px] text-ink">
                <strong className="tabular-nums">{total}</strong>{" "}
                {total === 1 ? "rilievo" : "rilievi"}
                {report.chapters_total
                  ? report.chapters_total === 1
                    ? report.scope_title
                      ? ` in «${report.scope_title}»`
                      : " in una sezione"
                    : ` su ${report.chapters_total} sezioni`
                  : ""}
                {/* Quanto è costata questa lettura: serve a scegliere, la
                    volta dopo, fra una parte sola e l'intero documento. */}
                {durata != null && (
                  <span className="numa-meta" data-testid="review-duration">
                    {" · "}letti in <span className="tabular-nums">{durataInParole(durata)}</span>
                  </span>
                )}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  onClick={async () => {
                    // Spuntate quelle che non costano nulla: le sostituzioni
                    // dirette e la struttura. Le riscritture del modello si
                    // scelgono a mano, perché si pagano.
                    setChosen(Object.fromEntries(
                      groups
                        .filter((g) => applicableBy[g.key] || structuralBy[g.key])
                        .map((g) => [g.key, true])
                    ));
                    /*
                      Le meccaniche si chiedono adesso, se non sono già note.

                      Arrivavano da una richiesta a parte, partita quando il
                      rapporto era pronto: chi premeva subito trovava l'elenco
                      vuoto, nessuna spuntata, e dopo aver «applicato tutto» si
                      ritrovava centoquaranta apostrofi ancora da sistemare.
                    */
                    let meccaniche = tidyFixes;
                    if (!meccaniche.length) {
                      meccaniche = await checks
                        .tidyAvailable(thesis.thesis_id, scopeId)
                        .then((res) => (res.fixes || []).filter((f) => f.count > 0))
                        .catch(() => []);
                      setTidyFixes(meccaniche);
                    }
                    setChosenTidy(Object.fromEntries(meccaniche.map((f) => [f.key, true])));
                    setBulkOpen(true);
                  }}
                  disabled={!total}
                  data-testid="apply-all-open"
                  title={applicableTotal
                    ? "Applica in un colpo solo le correzioni che possono entrare nel testo"
                    : "Vedi quali rilievi si possono applicare da soli e quali no"}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Applica tutte le correzioni
                  {applicableTotal > 0 && (
                    <span className="ml-1 rounded bg-on-solid/15 px-1.5 py-px text-2xs tabular-nums">
                      {applicableTotal}
                    </span>
                  )}
                </Button>
                <Button variant="outline" size="sm" onClick={download} disabled={!total}
                        data-testid="download-review"
                        title="Scarica il PDF con tutte le categorie, anche quelle non aperte">
                  <Download className="h-3.5 w-3.5" /> Scarica la revisione (PDF)
                </Button>
              </div>
            </div>

            {total === 0 ? (
              <div className="mt-6 rounded-md border border-dashed border-line-strong p-8 text-center">
                <FileText className="mx-auto h-8 w-8 text-ink-subtle" />
                <p className="mt-3 text-[14px] text-ink">Nessun rilievo.</p>
                <p className="numa-meta mt-1">
                  {aiDone
                    ? "NUMA ha riletto il documento e non ha trovato nulla da segnalare."
                    : "I controlli automatici non hanno trovato problemi di forma."}
                </p>
              </div>
            ) : (
              <>
                {/*
                  One category at a time.

                  A single list of everything meant scrolling past nine hundred
                  readability notes to reach the structural ones, which is the
                  same as not having the structural ones. The PDF still carries
                  every category, whichever is open here.
                */}
                <div className="mt-6 flex flex-wrap gap-1.5" role="tablist">
                  {groups.map((group) => {
                    const active = group.key === openGroup;
                    const origin = ORIGIN[group.key];
                    return (
                      <button
                        key={group.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        data-testid={`review-tab-${group.key}`}
                        onClick={() => setOpenGroup(active ? "" : group.key)}
                        className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-[13px] transition-colors ${
                          active
                            ? "border-brand bg-brand-soft font-medium text-brand"
                            : "border-line-strong text-ink-muted hover:bg-surface-2 hover:text-ink"
                        }`}
                      >
                        {group.label}
                        <span className={`rounded px-1.5 py-px text-2xs tabular-nums ${
                          active ? "bg-brand text-on-solid" : "bg-surface-2 text-ink-subtle"
                        }`}>
                          {/* «150+» e non «3799»: oltre il tetto si mostrano i
                              primi, e il numero esatto non aiuterebbe nessuno. */}
                          {group.findings.length}{group.more ? "+" : ""}
                        </span>
                        {origin && <Sparkles className="h-3 w-3 text-assistant" />}
                      </button>
                    );
                  })}
                </div>

                {current && (
                  <section className="mt-4" data-testid={`review-group-${current.key}`}>
                    {current.description && (
                      <p className="numa-meta leading-relaxed">{current.description}</p>
                    )}
                    {current.more > 0 && (
                      <p className="numa-meta mt-1 leading-relaxed" data-testid="altri-rilievi">
                        Su un documento lungo i rilievi di una stessa categoria sono
                        centinaia: qui ci sono i primi {current.findings.length}, e altri{" "}
                        <strong className="text-ink">{current.more}</strong> restano fuori.
                        Correggi questi e rifai la revisione per vedere i seguenti.
                      </p>
                    )}
                    <ul className="mt-2.5 space-y-2">
                      {current.findings.map((finding) => {
                        const meta = SEVERITY[finding.severity] || SEVERITY.info;
                        const Icon = meta.icon;
                        // Only where a real replacement exists: most checks put
                        // advice in `suggestion`, which is not something that can
                        // be written into the document.
                        const canApply = finding.replacement && finding.excerpt && finding.chapter_id;
                        // A structural correction carries what to do with it.
                        const canFix = !!finding.fix;
                        const canLocate = finding.chapter_id && (finding.excerpt || finding.position != null);
                        return (
                          <li
                            key={finding.id}
                            className="rounded-md border border-line bg-surface px-3.5 py-3"
                          >
                            <div className="flex items-start gap-2.5">
                              <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${meta.tone}`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-[13.5px] leading-snug text-ink">{finding.title}</p>
                                {finding.excerpt && (
                                  <p className="mt-1.5 border-l-2 border-line-strong pl-2.5 font-serif-display text-[13px] leading-relaxed text-ink-muted">
                                    {finding.excerpt}
                                  </p>
                                )}
                                {finding.detail && (
                                  <p className="numa-meta mt-1.5 leading-relaxed">{finding.detail}</p>
                                )}
                                <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-2xs text-ink-subtle">
                                  {canLocate && (
                                    <button
                                      type="button"
                                      data-testid={`locate-${finding.id}`}
                                      onClick={() => apriNelTesto(finding)}
                                      className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
                                    >
                                      <Crosshair className="h-3 w-3" />
                                      Vedi nel testo
                                    </button>
                                  )}
                                  {/*
                                    La pagina, non la sezione.

                                    «Introduzione» non aiuta a trovare niente in
                                    un documento di trecento pagine; «pagina 147»
                                    sì, ed è il numero che l'autore ha davanti
                                    anche nella sua copia in Word.
                                  */}
                                  {finding.page ? (
                                    <span title="Pagina del documento che hai importato">
                                      pagina {finding.page}
                                    </span>
                                  ) : null}
                                  {canApply && !applied[finding.id] && (
                                    <button
                                      type="button"
                                      data-testid={`apply-${finding.id}`}
                                      onClick={() => apply(finding)}
                                      disabled={applying === finding.id}
                                      className="inline-flex items-center gap-1 font-medium text-brand hover:underline disabled:opacity-60"
                                    >
                                      {applying === finding.id
                                        ? <Loader2 className="h-3 w-3 animate-spin" />
                                        : <Check className="h-3 w-3" />}
                                      Applica al testo
                                    </button>
                                  )}
                                  {canFix && !applied[finding.id] && (
                                    <button
                                      type="button"
                                      data-testid={`fix-${finding.id}`}
                                      onClick={() => applyStructure(finding)}
                                      disabled={applying === finding.id}
                                      title="Allinea i livelli dei titoli senza toccare il testo"
                                      className="inline-flex items-center gap-1 font-medium text-brand hover:underline disabled:opacity-60"
                                    >
                                      {applying === finding.id
                                        ? <Loader2 className="h-3 w-3 animate-spin" />
                                        : <Check className="h-3 w-3" />}
                                      Allinea i titoli
                                    </button>
                                  )}
                                  {/* An observation with no replacement of its
                                      own: the model writes one, and it is shown
                                      before anything is changed. */}
                                  {!canApply && !canFix && !applied[finding.id] && finding.excerpt
                                    && finding.chapter_id && (
                                    <button
                                      type="button"
                                      data-testid={`rewrite-${finding.id}`}
                                      onClick={() => proposeRewrite(finding)}
                                      disabled={rewriting === finding.id}
                                      title="NUMA propone una riscrittura del passaggio: usa il modello collegato"
                                      className="inline-flex items-center gap-1 font-medium text-assistant hover:underline disabled:opacity-60"
                                    >
                                      {rewriting === finding.id
                                        ? <Loader2 className="h-3 w-3 animate-spin" />
                                        : <Sparkles className="h-3 w-3" />}
                                      Proponi una correzione
                                    </button>
                                  )}
                                  {!applied[finding.id] && (
                                    <button
                                      type="button"
                                      data-testid={`dismiss-${finding.id}`}
                                      onClick={() => ignora(finding)}
                                      disabled={ignorando === finding.id}
                                      title="Toglilo dall'elenco: non verrà più segnalato"
                                      className="inline-flex items-center gap-1 text-ink-subtle hover:text-ink disabled:opacity-60"
                                    >
                                      {ignorando === finding.id
                                        ? <Loader2 className="h-3 w-3 animate-spin" />
                                        : <XCircle className="h-3 w-3" />}
                                      Ignora
                                    </button>
                                  )}
                                  {applied[finding.id] && (
                                    <span className="inline-flex items-center gap-1 text-ok">
                                      <Check className="h-3 w-3" /> applicata
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>

                            {proposal?.id === finding.id && (
                              <div
                                className="mt-3 rounded-md border border-assistant/40 bg-assistant-soft/25 px-3 py-2.5"
                                data-testid={`proposal-${finding.id}`}
                              >
                                <p className="numa-eyebrow text-assistant">Proposta di NUMA</p>
                                <p className="mt-1 font-serif-display text-[13.5px] leading-relaxed text-ink">
                                  {proposal.replacement}
                                </p>
                                <div className="mt-2 flex flex-wrap justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setProposal(null)}
                                    className="rounded-md border border-line-strong px-2.5 py-1 text-xs text-ink-muted hover:bg-surface-2"
                                  >
                                    Lascia com&apos;è
                                  </button>
                                  <button
                                    type="button"
                                    data-testid={`accept-${finding.id}`}
                                    onClick={acceptProposal}
                                    disabled={applying === proposal.id}
                                    className="inline-flex items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-xs font-medium text-on-solid hover:bg-brand-hover disabled:opacity-60"
                                  >
                                    {applying === proposal.id
                                      ? <Loader2 className="h-3 w-3 animate-spin" />
                                      : <Check className="h-3 w-3" />}
                                    Sostituisci
                                  </button>
                                </div>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}

                {!current && (
                  <p className="numa-meta mt-4">
                    Scegli una categoria per leggerne i rilievi. Il PDF li contiene tutti.
                  </p>
                )}
              </>
            )}

            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogContent data-testid="apply-all-dialog" className="max-w-lg rounded-md">
                <DialogHeader>
                  <DialogTitle className="font-serif-display text-xl">
                    Quali correzioni applicare
                  </DialogTitle>
                  <DialogDescription className="numa-meta">
                    Entrano nel testo solo le categorie che propongono una sostituzione
                    precisa. Le altre danno indicazioni da valutare, e restano in elenco.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-1.5">
                  {/* The mechanical ones: no judgement involved, and the only
                      corrections that can be applied without a model. */}
                  {tidyFixes.map((fix) => (
                    <label
                      key={fix.key}
                      className="flex cursor-pointer items-start gap-2.5 rounded-md border border-line px-3 py-2.5 hover:bg-surface-2"
                    >
                      <Checkbox
                        checked={!!chosenTidy[fix.key]}
                        data-testid={`apply-tidy-${fix.key}`}
                        onCheckedChange={(v) =>
                          setChosenTidy((prev) => ({ ...prev, [fix.key]: v === true }))}
                        className="mt-0.5"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13.5px] font-medium text-ink">
                          {fix.label}
                          <span className="ml-1.5 text-2xs font-normal tabular-nums text-ink-subtle">
                            {fix.count}
                          </span>
                        </span>
                        <span className="numa-meta mt-0.5 block leading-snug">{fix.detail}</span>
                      </span>
                    </label>
                  ))}

                  {orderedForApply.map((group) => {
                    const direct = applicableBy[group.key];
                    const viaModel = advisableBy[group.key];
                    const structural = structuralBy[group.key];
                    const usable = direct + viaModel + structural;
                    return (
                      <label
                        key={group.key}
                        className={`flex items-start gap-2.5 rounded-md border px-3 py-2.5 ${
                          usable
                            ? "cursor-pointer border-line hover:bg-surface-2"
                            : "border-line bg-surface-2/40"
                        }`}
                      >
                        <Checkbox
                          checked={!!chosen[group.key]}
                          disabled={!usable}
                          data-testid={`apply-all-${group.key}`}
                          onCheckedChange={(v) =>
                            setChosen((prev) => ({ ...prev, [group.key]: v === true }))}
                          className="mt-0.5"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-1.5 text-[13.5px] font-medium text-ink">
                            {group.label}
                            {viaModel > 0 && (
                              <span className="inline-flex items-center gap-1 rounded bg-assistant-soft px-1.5 py-px text-2xs font-normal text-assistant">
                                <Sparkles className="h-2.5 w-2.5" /> usa l&apos;AI
                              </span>
                            )}
                          </span>
                          <span className="numa-meta mt-0.5 block leading-snug">
                            {[
                              direct > 0 && `${direct} da applicare direttamente`,
                              structural > 0 &&
                                `${structural} sulla struttura (titoli e sezioni, senza AI)`,
                              viaModel > 0 &&
                                `${viaModel} da far riscrivere al modello (una chiamata ciascuno)`,
                            ].filter(Boolean).join(" · ")}
                            {!usable && `${group.findings.length} rilievi su cui non si può intervenire in blocco`}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>

                {!applicableTotal && !Object.values(advisableBy).some(Boolean)
                  && !Object.values(structuralBy).some(Boolean) && (
                  <p className="rounded-md border border-line bg-surface-2 px-3 py-2.5 text-[13px] leading-relaxed text-ink-muted">
                    Al momento non c&apos;è nulla da applicare da solo: i rilievi presenti
                    dicono cosa guardare, non con che cosa sostituirlo. Le correzioni
                    applicabili arrivano dalla rilettura ortografica, che richiede un
                    modello collegato, e dalle sistemazioni tipografiche qui sopra.
                  </p>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="numa-meta">
                    {chosenCount || chosenAdvice.length || chosenStructure.length
                      ? [
                          chosenCount ? `${chosenCount} da applicare` : "",
                          chosenStructure.length
                            ? `${chosenStructure.length} sulla struttura`
                            : "",
                          chosenAdvice.length
                            ? `${Math.min(chosenAdvice.length, 20)} da far riscrivere al modello`
                            : "",
                        ].filter(Boolean).join(" · ") +
                        ". La versione precedente resta in Versioni."
                      : "Nessuna categoria selezionata."}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setBulkOpen(false)}>Annulla</Button>
                    <Button
                      onClick={applyAll}
                      disabled={
                        (!chosenCount && !chosenAdvice.length && !chosenStructure.length)
                        || bulkRunning
                      }
                      data-testid="apply-all-confirm"
                    >
                      {bulkRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />}
                      Applica
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <p className="mt-8 border-t border-line pt-5 text-2xs leading-relaxed text-ink-subtle">
              I rilievi letti dal modello sono proposte di un lettore, non errori accertati:
              valutali prima di accoglierli. La responsabilità scientifica del testo resta tua.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
